import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import metadata from './PolicyCommonDetails.metadata.json5';
import './PolicyCommonDetails.messages';
import styles from './PolicyCommonDetails.module.scss';

function PolicyCommonDetails(props) {
    const { value: policyCommonDetails } = props;

    const getMultilineDiscountAmount = useCallback(() => {
        const productName = _.get(policyCommonDetails, 'productName');
        let policyAmount = null;
        if (productName === _.get(appConfig.LOBMultiLineDiscount, 'GLLine')) {
            policyAmount = _.get(policyCommonDetails, 'policyLineDiscounts.liabilityMultiLineDiscount', null);
        }
        if (productName === _.get(appConfig.LOBMultiLineDiscount, 'BOPLine')) {
            policyAmount = _.get(policyCommonDetails, 'policyLineDiscounts.boplineDiscounts[0].multiLinePropertyDiscount', null);
        }
        if (productName === _.get(appConfig.LOBMultiLineDiscount, 'WCLine')) {
            policyAmount = _.get(policyCommonDetails, 'policyLineDiscounts.wcMultiLineDiscount', null);
        }
        return policyAmount;
    }, [policyCommonDetails]);

    const seminarURL = useMemo(() => {
        return _.get(appConfig, `tdic.portalSeminarURL.${_.toLower(process.env.NODE_ENV)}`, _.get(appConfig, 'tdic.portalSeminarURL.development'));
    },
    []);

    const getMultilineDiscountType = useCallback(() => {
        const multiLineDiscountTpe = _.get(policyCommonDetails, 'policyLineDiscounts.multiLineDiscountType');
        const policyAmount = getMultilineDiscountAmount();
        if (policyAmount) {
            if (multiLineDiscountTpe === 'PL-CP') {
                return 'MultiLine Property';
            }
            if (multiLineDiscountTpe === 'All') {
                return 'MultiLine Optimum';
            }
            if (multiLineDiscountTpe === 'PL-WC') {
                return 'MultiLine Workers Compensation';
            }
            if (_.isNil(multiLineDiscountTpe)) return null;
        }
        return null;
    }, [getMultilineDiscountAmount, policyCommonDetails]);

    const riskManagementVisibility = useCallback(() => {
        const policyOffering = _.get(policyCommonDetails, 'offeringCode');
        const claimsMadeOffering = _.get(appConfig, 'TDICOfferingCodes.PL_CLAIMS_MADE_CODE');
        const occurenceOffering = _.get(appConfig, 'TDICOfferingCodes.PL_OCCURENCE_CODE');
        return (policyOffering === claimsMadeOffering || policyOffering === occurenceOffering);
    }, [policyCommonDetails]);

    const discountLabel = useCallback(() => {
        return (!(_.get(policyCommonDetails, 'policyLineDiscounts.riskManagementDiscount', false)) && (riskManagementVisibility()))
            || (_.isNil(getMultilineDiscountType()) && (_.get(policyCommonDetails, 'offeringCode') !== _.get(appConfig, 'TDICOfferingCodes.PL_CYBERLIAB_CODE')));
    });

    const overrides = {
        specialtyType: {
            visible: riskManagementVisibility()
        },
        infoLink: {
            href: seminarURL
        },
        riskManagementDiscount: {
            visible: _.includes(appConfig.LOBRiskManagement, _.get(policyCommonDetails, 'offeringCode'))
             && (_.get(policyCommonDetails, 'policyLineDiscounts.riskManagementDiscount') === true)
        },
        multiLineDiscounts: {
            content: getMultilineDiscountType(),
            visible: _.get(policyCommonDetails, 'offeringCode') !== _.get(appConfig, 'TDICOfferingCodes.PL_CYBERLIAB_CODE')
        },
        odaMembershipDiscount: {
            visible: _.get(policyCommonDetails, 'policyLineDiscounts.sdamemberDiscount', false)
        },
        infoLabel: {
            visible: discountLabel()
        },
        multilineInfoLabel: {
            visible: (_.isNull(getMultilineDiscountType())) && (_.get(policyCommonDetails, 'offeringCode')
            !== _.get(appConfig, 'TDICOfferingCodes.PL_CYBERLIAB_CODE'))
        },
        agentContainer: {
            visible: !_.get(policyCommonDetails, 'policyLineDiscounts.riskManagementDiscount', false) && riskManagementVisibility()
        },
        discountSectionText: {
            visible: _.get(policyCommonDetails, 'policyLineDiscounts.riskManagementDiscount') || _.get(policyCommonDetails, 'policyLineDiscounts.sdamemberDiscount') || !_.isNull(getMultilineDiscountType())
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.componentContent, policyCommonDetails,
                id, path, overrides);
        },
        [overrides, policyCommonDetails]
    );

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={policyCommonDetails}
            overrideProps={overrides}
            resolveValue={readValue}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

PolicyCommonDetails.propTypes = {
    value: PropTypes.shape({})
};

PolicyCommonDetails.defaultProps = {
    value: {}
};

export default PolicyCommonDetails;
